import { View } from 'backbone';
import htmlTemplate from 'properties/theme/export-import-theme.ejs';
import listService from 'ListService';
import NotificationService from 'NotificationService.js';
import util from 'util/Util';
import themeService from 'ThemeService';
import defaultTheme from 'defaultTheme';
import pageSyncService from 'PageSyncService';
import zLib from 'util/ZLib.js';

const THEME_SIGNATURE = "perfectapps-theme-v1";
const FONT_GROUPS = {
    'text': { font: 'textFont', size: 'textFontSize', weight: 'textWeight', style: 'textStyle' },
    'smallText': { font: 'smallTextFont', size: 'smallTextFontSize', weight: 'smallTextWeight', style: 'smallTextStyle' },
    'largeHeading': { font: 'largeHeadingFont', size: 'largeHeadingFontSize', weight: 'largeHeadingWeight', style: 'largeHeadingStyle' },
    'mediumHeading': { font: 'mediumHeadingFont', size: 'mediumHeadingFontSize', weight: 'mediumHeadingWeight', style: 'mediumHeadingStyle' },
    'smallHeading': { font: 'smallHeadingFont', size: 'smallHeadingFontSize', weight: 'smallHeadingWeight', style: 'smallHeadingStyle' },
    'link': { font: 'linkFont', size: 'linkFontSize', weight: 'linkWeight', style: 'linkStyle' },
    'input': { font: 'inputFont', size: 'inputFontSize', weight: 'inputWeight', style: 'inputStyle' },
    'button': { font: 'buttonFont', size: 'buttonFontSize', weight: 'buttonTextWeight', style: 'buttonTextStyle' },
    'checkbox': { font: 'checkboxFont', size: 'checkboxFontSize', weight: 'checkboxTextWeight', style: 'checkboxTextStyle' },
    'group': { font: 'groupFont', size: 'groupFontSize', weight: 'groupTextWeight', style: 'groupTextStyle' },
    'tableHeader': { font: 'tableHeaderFont', size: 'tableHeaderFontSize', weight: 'tableHeaderTextWeight', style: 'tableHeaderTextStyle' },
};

const THEME_PROPERTIES_NON_FONT = [
    'backgroundColor', 'textColor', 'smallTextColor', 'largeHeadingColor', 'mediumHeadingColor', 'smallHeadingColor',
    'linkColor', 'inputTextColor', 'inputBorderColor', 'inputBorderStyle', 'inputBorderWidth', 'inputBorderRadius', 'inputBgColor',
    'buttonStyle', 'buttonTextColor', 'buttonTextOverColor', 'buttonTextDownColor', 'buttonBgColor', 'buttonBgGradientColor',
    'buttonBgOverColor', 'buttonBgGradientOverColor', 'buttonBgDownColor', 'buttonBgGradientDownColor', 'buttonRadius',
    'selectionBgColor', 'selectionTextColor', 'selectionTextOverColor', 'checkBgTransparent',
    'groupTextColor', 'groupTitleBg', 'groupBorderColor', 'groupBorderStyle', 'groupBorderSize', 'groupBorderRadius', 'groupBgColor', 'groupBgTransparent',
    'tableHeaderTextColor', 'tableHeaderBgColor', 'tableHeaderBgGradientColor', 'tableRowColor1', 'tableRowColor2',
    'tableBorderColor', 'tableBorderStyle', 'tableBorderSize', 'tableBorderRadius', 'tableBgColor',
    'seperatorColor', 'seperatorSize'
];

class ExportImportView extends View {

    initialize( opts ) {
        this.options = opts || {};
        this.template = htmlTemplate;
        this.tagName = 'div';
        this.id = opts.el;

        this.events = {
            'click #importTheme': 'importTheme',
            'click #exportTheme': 'exportTheme',
            'click #resetTheme' : 'resetTheme',
        };

        return this;
    }

    resetTheme(ev){
        const warning = "Are you sure you want to reset the theme? This will override all current theme settings.";
        let simpleModal = util.createDialogDeleteConfirmation("Import Theme", warning, true);        
        listService.isUndoRedoEnabled = false;

        simpleModal.render().then((result) => {
            if (result === 'Ok') {

                try {
                    const defaultThemeObject = defaultTheme.toJS();
                    const modelUpdateData = {};
        
                    themeService.reset();
        
                    for (const tildeKey in defaultThemeObject) {
                        if (defaultThemeObject.hasOwnProperty(tildeKey)) {
                            const value = defaultThemeObject[tildeKey];
                            const modelKey = tildeKey.substring(1);
        
                            if (FONT_GROUPS[modelKey]) {
                                const groupProps = FONT_GROUPS[modelKey];
                                const parts = value.split(',').map(p => p.trim());
                                if (parts.length === 4) {
                                    modelUpdateData[groupProps.font] = parts[0];
                                    modelUpdateData[groupProps.size] = parts[1];
                                    modelUpdateData[groupProps.weight] = parts[2];
                                    modelUpdateData[groupProps.style] = parts[3];
                                }
                            } else {
                                modelUpdateData[modelKey] = value;
                            }
                        }
                    }
        
                    this.model.set(modelUpdateData);
        
                    pageSyncService.renderCurrentPageView();
                    NotificationService.success('Theme reset successfully.');
        
                } catch (err) {
                    console.error("Failed to reset theme:", err);
                    NotificationService.error(`Theme Reset Failed: ${err.message}`);
                }
                
            }
            listService.isUndoRedoEnabled = true;
        });
    }

    importTheme(ev) {

        const warning = "Are you sure you want to import the theme? This will override all current theme settings.";
        let simpleModal = util.createDialogDeleteConfirmation("Import Theme", warning, true);

        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pa';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            try {
                const content = await file.text();
                let themeData;

                try {
                    const json = zLib.decompressForm( content );
                    themeData = JSON.parse(json);
                } catch (parseError) {
                    console.error("JSON parsing failed:", parseError);
                    throw new Error("The selected file is not a valid theme file.");
                }

                // Check for signature property.
                if (themeData._file_type !== THEME_SIGNATURE) {
                    throw new Error("The selected file is not a valid theme file.");
                }

                simpleModal.render().then((result) => {
                    if (result === 'Ok') {

                        // Remove the signature and name properties so they're not set on the model
                        delete themeData._file_type;
                        delete themeData.name;

                        themeService.reset();

                        const modelUpdateData = {};

                        for (const key in themeData) {
                            if (themeData.hasOwnProperty(key)) {
                                let value = themeData[key];
                                const tildeKey = `~${key}`;
        
                                if (FONT_GROUPS[key]) {
                                    themeService.updateItem(value, tildeKey);
                                    
                                    const groupProps = FONT_GROUPS[key];
                                    const parts = value.split(',').map(p => p.trim());
                                    if (parts.length === 4) {
                                        modelUpdateData[groupProps.font] = parts[0];
                                        modelUpdateData[groupProps.size] = parts[1];
                                        modelUpdateData[groupProps.weight] = parts[2];
                                        modelUpdateData[groupProps.style] = parts[3];
                                    }
                                } else {
                                    modelUpdateData[key] = value;
        
                                    if (tildeKey === '~groupBorder') {
                                        let propArray = value.split(',');
                                        if (propArray.length === 2) value += ',0';
                                    }
                                    themeService.updateItem(value, tildeKey);
                                }
                            }
                        }

                        this.model.set(themeData);
                        pageSyncService.renderCurrentPageView();
                        NotificationService.success('Theme loaded successfully.');
                    }
                });

            } catch (err) {
                console.log("Failed to import theme:", err);
                NotificationService.error(`Theme Import Failed: ${err.message}`);
            }
        };
        input.click();
    }

    exportTheme(ev){
        const allAttributes = this.model.toJSON();
        const themeAttributes = {};

        THEME_PROPERTIES_NON_FONT.forEach(key => {
            if (allAttributes.hasOwnProperty(key)) {
                let value = allAttributes[key];               
                themeAttributes[key] = value;
            }
        });
        
        for (const groupKey in FONT_GROUPS) {
            const groupProps = FONT_GROUPS[groupKey];
            const font = allAttributes[groupProps.font] || 'Verdana';
            const size = allAttributes[groupProps.size] || '10';
            const weight = allAttributes[groupProps.weight] || 'normal';
            const style = allAttributes[groupProps.style] || 'normal';
            
            const compositeFontString = `${font}, ${size}, ${weight}, ${style}`;
            
            themeAttributes[groupProps.font] = compositeFontString;
        }

        
        themeAttributes.name = allAttributes.name || "Untitled_Theme";

        // Add a hidden signature property to the file.
        themeAttributes._file_type = THEME_SIGNATURE;

        const themeJson = JSON.stringify(themeAttributes, null, 2);
        const fileContent = zLib.compressForm(themeJson);
        const formName = listService.getObject("aForm").model.get("name").replace(/\s/g, "_");
        const fileName = `${formName}_theme.pa`;

        const blob = new Blob([fileContent], { type: 'application/json;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    }

    render() {
        this.$el.empty();
        if(this.template) {
            this.$el.html(this.template(this.model.toJSON()));
        }

        this.delegateEvents();
        return this;
    }
}

export default ExportImportView;
